import { ProfileOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { App, Badge, Button, Popover, Table } from 'antd';
import { useEffect, useState } from 'react';
import { MediaPreviewButton } from '../../file/components/media-preview-button';
import { useFissionPageData } from '../../fission/store';
import { videoModelSpecs } from '../../video-model/model-spec';
import { getTaskListQueryOptions } from '../api';
import { TaskStatusCode, TaskType } from '../model';
import { TaskStatus } from './task-status';

export const RunningTaskListButton = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const [_0, setFissionPageData] = useFissionPageData();
  const [pendingTaskIds, setPendingTaskIds] = useState<Set<string>>(
    () => new Set(),
  );

  const tasksQueryResult = useQuery({
    ...getTaskListQueryOptions({
      status: [
        TaskStatusCode.任务执行中,
        TaskStatusCode.未开始,
        TaskStatusCode.分析视频2,
      ],
    }),
    refetchInterval: 15000,
  });

  useEffect(() => {
    setPendingTaskIds((ids) => {
      const set = new Set([
        ...ids,
        ...(tasksQueryResult.data?.data
          .filter((item) => item.type === TaskType.批量图片翻译)
          .map((item) => item.id) ?? []),
      ]);

      return set;
    });
  }, [tasksQueryResult.data]);

  const taskStatusQueryResult = useQuery({
    enabled: !!pendingTaskIds.size,
    ...getTaskListQueryOptions({
      task_ids: [...pendingTaskIds],
    }),
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (!taskStatusQueryResult.data?.data.length) {
      return;
    }

    for (const task of taskStatusQueryResult.data.data) {
      if (TaskStatus.isSettled(task.status)) {
        setPendingTaskIds((idSet) => {
          idSet.delete(task.id);
          return new Set(idSet);
        });
      }

      if (TaskStatus.isSucceeded(task.status)) {
        switch (task.type) {
          case TaskType.脚本裂变:
            notification.success({
              key: task.id,
              title: '脚本裂变完成',
              duration: 0,
              description: `脚本裂变任务 ${task.id} 完成`,
              onClick: () => {
                if (!task.generate_data) {
                  return;
                }

                setFissionPageData({
                  ...task.generate_data,
                });
                navigate({ to: '/fission' });
              },
            });
            break;
          default:
            notification.success({
              key: task.id,
              title: '任务完成',
              duration: 5,
              showProgress: true,
              pauseOnHover: true,
              description: `任务 ${task.id} 生成成功`,
              actions: task.filename ? (
                <MediaPreviewButton
                  variant="text"
                  size="small"
                  href={task.filename}
                >
                  {task.type === TaskType.图片生成 ? '查看图片' : '查看视频'}
                </MediaPreviewButton>
              ) : null,
            });
            break;
        }
      } else if (TaskStatus.isFailed(task.status)) {
        notification.error({
          key: task.id,
          title: '任务失败',
          duration: 0,
          description: `任务 ${task.id} 生成失败`,
        });
      }
    }
  }, [taskStatusQueryResult.data, navigate, notification, setFissionPageData]);

  const list = (
    <Table
      tableLayout="fixed"
      classNames={{ root: 'w-96 max-w-[80vw]' }}
      rowKey={(row) => row.id}
      size="small"
      dataSource={tasksQueryResult.data?.data}
      columns={[
        {
          render: (_, item) => {
            return (
              <div className="w-full">
                <div className="truncate">任务 {item.id}</div>
                <div className="truncate text-xs text-primary-500">
                  {videoModelSpecs.get(item.model)?.name ?? item.model}
                </div>
                <div className="truncate text-xs text-primary-500">
                  {new Date(item.created_at).toLocaleString()}
                </div>
              </div>
            );
          },
        },
        {
          width: 128,
          align: 'left',
          render: (_, item) => {
            return <TaskStatus task={item}></TaskStatus>;
          },
        },
      ]}
      showHeader={false}
      pagination={false}
    ></Table>
  );

  return (
    <Popover
      title="任务列表"
      placement="bottomRight"
      classNames={{ content: 'max-h-96 overflow-auto' }}
      trigger="click"
      content={list}
      forceRender
    >
      <Button
        variant="filled"
        color="default"
        icon={
          <Badge dot={!!tasksQueryResult.data?.data.length}>
            <ProfileOutlined />
          </Badge>
        }
      >
        任务中心
      </Button>
    </Popover>
  );
};
